if (!customElements.get('product-pair-card')) {
  customElements.define(
    'product-pair-card',
    class ProductPairCard extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;

        this.variantInput = this.querySelector('[data-pair-variant-id]');
        this.currentPrice = this.querySelector('[data-pair-current-price]');
        this.comparePrice = this.querySelector('[data-pair-compare-price]');
        this.imageElement = this.querySelector('.product-pair-card__image');
        this.unavailableElement = this.querySelector('[data-pair-unavailable]');

        const variantsElement = this.querySelector('[data-pair-variants]');

        try {
          this.variants = JSON.parse(variantsElement?.textContent || '[]');
        } catch (error) {
          console.error('Product Pair Card variants JSON:', error);
          this.variants = [];
        }

        this.optionInputs = Array.from(
          this.querySelectorAll('[data-pair-option-input]')
        );

        this.optionInputs.forEach((input) => {
          input.addEventListener(
            'change',
            this.onOptionChange.bind(this)
          );
        });

        this.updateVariant();
      }

      getSelectedOptions() {
        const groups = Array.from(
          this.querySelectorAll('[data-pair-option]')
        );

        return groups.map((group) => {
          const select = group.querySelector(
            'select[data-pair-option-input]'
          );

          if (select) {
            return select.value;
          }

          const checked = group.querySelector(
            'input[data-pair-option-input]:checked'
          );

          return checked?.value || '';
        });
      }

      findVariant() {
        const selectedOptions = this.getSelectedOptions();

        if (!selectedOptions.length) {
          return (
            this.variants.find((variant) => variant.available) ||
            this.variants[0] ||
            null
          );
        }

        return (
          this.variants.find((variant) => {
            if (!Array.isArray(variant.options)) {
              return false;
            }

            return selectedOptions.every(
              (value, index) => variant.options[index] === value
            );
          }) || null
        );
      }

      onOptionChange() {
        this.updateVariant();
      }

      updateOptionLabels() {
        const groups = Array.from(
          this.querySelectorAll('[data-pair-option]')
        );

        groups.forEach((group) => {
          const labelValue = group.querySelector(
            '[data-pair-option-label-value]'
          );

          if (!labelValue) return;

          const checkedInput = group.querySelector(
            'input[data-pair-option-input]:checked'
          );

          const select = group.querySelector(
            'select[data-pair-option-input]'
          );

          const currentVal =
            checkedInput?.value ||
            select?.value ||
            '';

          if (currentVal) {
            labelValue.textContent = `: ${currentVal}`;
          }
        });
      }

      updateVariant() {
        const variant = this.findVariant();
        const available = !!variant && variant.available === true;

        if (this.unavailableElement) {
          this.unavailableElement.hidden = available;
        }

        this.updateOptionLabels();

        if (!variant || !available) {
          if (this.variantInput) {
            this.variantInput.value = '';
          }

          this.dispatchVariantChange(null);
          return;
        }

        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        this.updatePrice(variant);
        this.updateImage(variant);
        this.updateOptionAvailability();
        this.dispatchVariantChange(variant);
      }

      updatePrice(variant) {
        if (this.currentPrice) {
          this.currentPrice.innerHTML = variant.price_money;
        }

        if (!this.comparePrice) {
          return;
        }

        const hasComparePrice =
          Number(variant.compare_at_price) >
          Number(variant.price);

        this.comparePrice.hidden = !hasComparePrice;

        this.comparePrice.innerHTML = hasComparePrice
          ? variant.compare_at_price_money
          : '';
      }

      updateImage(variant) {
        if (!this.imageElement || !variant.featured_image) {
          return;
        }

        this.imageElement.src = variant.featured_image;
        this.imageElement.removeAttribute('srcset');
      }

      updateOptionAvailability() {
        const selected = this.getSelectedOptions();

        const groups = Array.from(
          this.querySelectorAll('[data-pair-option]')
        );

        groups.forEach((group, groupIndex) => {
          const inputs = Array.from(
            group.querySelectorAll('[data-pair-option-input]')
          );

          inputs.forEach((input) => {
            const candidateOptions = [...selected];

            candidateOptions[groupIndex] = input.value;

            const exists = this.variants.some((variant) => {
              if (!variant.available) {
                return false;
              }

              return candidateOptions.every(
                (value, optionIndex) => {
                  if (optionIndex === groupIndex) {
                    return (
                      variant.options[optionIndex] === value
                    );
                  }

                  if (!value) {
                    return true;
                  }

                  return (
                    variant.options[optionIndex] === value
                  );
                }
              );
            });

            input.disabled = !exists;
          });
        });
      }

      dispatchVariantChange(variant) {
        this.dispatchEvent(
          new CustomEvent('pair-card:variant-change', {
            bubbles: true,
            detail: {
              variant
            }
          })
        );
      }
    }
  );
}

if (!customElements.get('product-pair-bundle')) {
  customElements.define(
    'product-pair-bundle',
    class ProductPairBundle extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;

        this.selects = Array.from(
          this.querySelectorAll('[data-pair-select]')
        );

        this.submitButton = this.querySelector('[data-pair-submit]');
        this.errorMessage = this.querySelector('[data-pair-error]');

        this.allowSameProduct =
          this.dataset.allowSame === 'true';

        this.totalElement = this.querySelector(
          '[data-pair-total]'
        );

        this.subtotalElement = this.querySelector(
          '[data-pair-subtotal]'
        );

        this.savingsBadge = this.querySelector(
          '[data-pair-savings-badge]'
        );

        this.savingsOffElement = this.querySelector(
          '[data-pair-savings-off]'
        );

        this.savingsOffDiv = this.querySelector(
          '[data-savings-off]'
        );

        this.extraDiscount =
          parseFloat(this.dataset.extraDiscount) || 0;

        this.selects.forEach((select) => {
          select.addEventListener(
            'change',
            this.onProductChange.bind(this)
          );
        });

        this.addEventListener(
          'pair-card:variant-change',
          this.onVariantChange.bind(this)
        );

        this.submitButton?.addEventListener(
          'click',
          this.onSubmitHandler.bind(this)
        );

        requestAnimationFrame(() => {
          this.syncVisibleProducts();
          this.updateDuplicateOptions();
          this.updateSubmitState();
          this.updateSavingsBadge();
          this.updateTotal();
        });
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      formatMoneyWithoutTrailingZeros(cents) {
        return theme.Currency
          .formatMoney(
            cents,
            theme.settings?.moneyFormat || undefined
          )
          .replace(/([.,]00)(?=\s*[^\d]*$)/, '');
      }

      onProductChange(event) {
        if (!this.allowSameProduct) {
          this.resolveDuplicateSelection(
            event.currentTarget
          );
        }

        this.updateDuplicateOptions();
        this.syncVisibleProducts();
        this.handleErrorMessage();
        this.updateSubmitState();
        this.updateSavingsBadge();
        this.updateTotal();
      }

      resolveDuplicateSelection(changedSelect) {
        if (this.selects.length < 2) {
          return;
        }

        const [firstSelect, secondSelect] =
          this.selects;

        if (
          firstSelect.value !==
          secondSelect.value
        ) {
          return;
        }

        const otherSelect =
          changedSelect === firstSelect
            ? secondSelect
            : firstSelect;

        const replacement = Array
          .from(otherSelect.options)
          .find(
            (option) =>
              !option.dataset.originalDisabled &&
              option.value !== changedSelect.value
          );

        if (replacement) {
          otherSelect.value = replacement.value;
        }
      }

      updateDuplicateOptions() {
        if (this.selects.length < 2) {
          return;
        }

        const [firstSelect, secondSelect] =
          this.selects;

        Array
          .from(firstSelect.options)
          .forEach((option) => {
            option.disabled =
              option.dataset.originalDisabled === 'true';
          });

        Array
          .from(secondSelect.options)
          .forEach((option) => {
            option.disabled =
              option.dataset.originalDisabled === 'true';
          });

        if (this.allowSameProduct) {
          return;
        }

        const firstValue =
          firstSelect.value;

        const secondValue =
          secondSelect.value;

        Array
          .from(firstSelect.options)
          .forEach((option) => {
            if (option.value === secondValue) {
              option.disabled = true;
            }
          });

        Array
          .from(secondSelect.options)
          .forEach((option) => {
            if (option.value === firstValue) {
              option.disabled = true;
            }
          });
      }

      syncVisibleProducts() {
        this.selects.forEach((select) => {
          this.updateSlot(
            select.dataset.pairSelect,
            select.value
          );
        });
      }

      updateSlot(slotIndex, handle) {
        const slot = this.querySelector(
          `[data-pair-slot="${slotIndex}"]`
        );

        if (!slot) return;

        slot
          .querySelectorAll('[data-pair-product]')
          .forEach((card) => {
            card.hidden =
              card.dataset.productHandle !== handle;
          });
      }

      getActiveCard(slotIndex) {
        const slot = this.querySelector(
          `[data-pair-slot="${slotIndex}"]`
        );

        if (!slot) {
          return null;
        }

        return (
          Array
            .from(
              slot.querySelectorAll(
                '[data-pair-product]'
              )
            )
            .find((card) => !card.hidden) ||
          null
        );
      }

      getActiveVariant(slotIndex) {
        const activeCard =
          this.getActiveCard(slotIndex);

        if (!activeCard) {
          return null;
        }

        const input = activeCard.querySelector(
          '[data-pair-variant-id]'
        );

        if (!input || !input.value) {
          return null;
        }

        return input.value;
      }

      getActiveVariantData(slotIndex) {
        const activeWrapper =
          this.getActiveCard(slotIndex);

        if (!activeWrapper) {
          return null;
        }

        const pairCard =
          activeWrapper.querySelector(
            'product-pair-card'
          );

        if (!pairCard) {
          return null;
        }

        let variantId =
          this.getActiveVariant(slotIndex);

        let variants =
          pairCard.variants;

        if (
          !Array.isArray(variants) ||
          variants.length === 0
        ) {
          const script = pairCard.querySelector(
            '[data-pair-variants]'
          );

          try {
            variants = JSON.parse(
              script?.textContent || '[]'
            );
          } catch (error) {
            console.error(
              'Product Pair Bundle variants JSON:',
              error
            );

            variants = [];
          }
        }

        if (!variants.length) {
          return null;
        }

        if (!variantId) {
          const availableVariant =
            variants.find(
              (variant) => variant.available
            ) ||
            variants[0];

          variantId =
            availableVariant?.id;
        }

        return (
          variants.find(
            (variant) =>
              String(variant.id) ===
              String(variantId)
          ) ||
          null
        );
      }

      updateSavingsBadge() {
        const variant1 =
          this.getActiveVariantData('1');

        const variant2 =
          this.getActiveVariantData('2');

        if (!variant1 || !variant2) {
          if (this.savingsBadge) {
            this.savingsBadge.innerHTML = '';
            this.savingsBadge.style.display =
              'none';
          }

          if (this.savingsOffElement) {
            this.savingsOffElement.textContent =
              '';
          }

          if (this.savingsOffDiv) {
            this.savingsOffDiv.classList.add(
              'none'
            );
          }

          return;
        }

        const getVariantSaving = (variant) => {
          const comparePrice =
            Number(
              variant.compare_at_price || 0
            );

          const price =
            Number(
              variant.price || 0
            );

          return comparePrice > price
            ? comparePrice - price
            : 0;
        };

        const bikesSavings =
          getVariantSaving(variant1) +
          getVariantSaving(variant2);

        if (this.savingsOffElement) {
          this.savingsOffElement.textContent =
            this.formatMoneyWithoutTrailingZeros(
              bikesSavings
            );
        }

        if (this.savingsOffDiv) {
          this.savingsOffDiv.classList.toggle(
            'hidden',
            bikesSavings <= 0
          );
        }

        const extraSavings = Math.round(
          this.extraDiscount * 100
        );

        const totalSavings =
          bikesSavings +
          extraSavings;

        if (this.savingsBadge) {
          if (totalSavings > 0) {
            const saveMoney =
              this.formatMoneyWithoutTrailingZeros(
                totalSavings
              );

            const rawSave =
              theme.strings?.save;

            const saveLabel =
              rawSave &&
              rawSave !== 'undefined' &&
              rawSave.trim() !== ''
                ? rawSave
                : 'Save';

            this.savingsBadge.innerHTML =
              `${saveLabel} ${saveMoney}`;

            this.savingsBadge.classList.remove(
              'hidden'
            );

            this.savingsBadge.style.display =
              'flex';
          } else {
            this.savingsBadge.style.display =
              'none';

            this.savingsBadge.innerHTML =
              '';
          }
        }
      }

      updateTotal() {
        if (!this.totalElement) {
          return;
        }

        const variant1 =
          this.getActiveVariantData('1');

        const variant2 =
          this.getActiveVariantData('2');

        if (!variant1 || !variant2) {
          this.totalElement.innerHTML = '';

          if (this.subtotalElement) {
            this.subtotalElement.innerHTML = '';
          }

          return;
        }

        const extraSavings = Math.round(
          this.extraDiscount * 100
        );

        const subtotal =
          Number(variant1.price || 0) +
          Number(variant2.price || 0);

        const total =
          subtotal - extraSavings;

        if (this.subtotalElement) {
          this.subtotalElement.innerHTML =
            this.formatMoneyWithoutTrailingZeros(
              subtotal
            );
        }

        this.totalElement.innerHTML =
          this.formatMoneyWithoutTrailingZeros(
            total
          );
      }

      onVariantChange() {
        this.handleErrorMessage();
        this.updateSubmitState();
        this.updateSavingsBadge();
        this.updateTotal();
      }

      updateSubmitState() {
        if (!this.submitButton) {
          return;
        }

        const variant1 =
          this.getActiveVariant('1');

        const variant2 =
          this.getActiveVariant('2');

        const valid =
          !!variant1 &&
          !!variant2;

        if (valid) {
          this.submitButton.removeAttribute(
            'aria-disabled'
          );
        } else {
          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (!this.submitButton) {
          return;
        }

        const variant1 =
          this.getActiveVariant('1');

        const variant2 =
          this.getActiveVariant('2');

        if (!variant1 || !variant2) {
          this.handleErrorMessage(
            'Please choose an available variant for both e-bikes.'
          );

          return;
        }

        if (
          !this.allowSameProduct &&
          this.selects[0]?.value ===
            this.selects[1]?.value
        ) {
          this.handleErrorMessage(
            'Please choose two different e-bike models.'
          );

          return;
        }

        const data = {
          items: [
            {
              id: Number(variant1),
              quantity: 1,
              properties: {
                _bundle: 'Added by bundle'
              }
            },
            {
              id: Number(variant2),
              quantity: 1,
              properties: {
                _bundle: 'Added by bundle'
              }
            }
          ]
        };

        this.handleErrorMessage();

        if (
          document.body.classList.contains(
            'template-cart'
          ) ||
          theme.settings.cartType === 'page'
        ) {
          theme.utils.postLink2(
            theme.routes.cart_add_url,
            {
              parameters: data
            }
          );

          return;
        }

        this.activeElement =
          event.currentTarget;

        let sectionsToBundle = [];

        document.documentElement.dispatchEvent(
          new CustomEvent(
            'cart:bundled-sections',
            {
              bubbles: true,
              detail: {
                sections:
                  sectionsToBundle
              }
            }
          )
        );

        const body = JSON.stringify({
          ...data,
          sections: sectionsToBundle,
          sections_url:
            window.location.pathname
        });

        this.submitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        this.submitButton.setAttribute(
          'aria-busy',
          'true'
        );

        try {
          const response = await fetch(
            theme.routes.cart_add_url,
            {
              ...theme.utils.fetchConfig(
                'javascript'
              ),
              body
            }
          );

          const parsedState =
            await response.json();

          if (parsedState.status) {
            this.handleErrorMessage(
              parsedState.description
            );

            document.dispatchEvent(
              new CustomEvent(
                'ajaxProduct:error',
                {
                  detail: {
                    errorMessage:
                      parsedState.description
                  }
                }
              )
            );

            return;
          }

          const cartResponse =
            await fetch(
              theme.routes.cart_url,
              {
                ...theme.utils.fetchConfig(
                  'json',
                  'GET'
                )
              }
            );

          const cartJson =
            await cartResponse.json();

          cartJson.sections =
            parsedState.sections;

          theme.pubsub.publish(
            theme.pubsub.PUB_SUB_EVENTS
              .cartUpdate,
            {
              source:
                'product-pair-bundle',
              cart: cartJson
            }
          );

          document.dispatchEvent(
            new CustomEvent(
              'ajaxProduct:added',
              {
                detail: {
                  product:
                    parsedState
                }
              }
            )
          );

          this.cartDrawer?.show(
            this.activeElement
          );
        } catch (error) {
          console.error(
            'Product Pair Bundle:',
            error
          );

          this.handleErrorMessage(
            'Unable to add the selected products to cart. Please try again.'
          );
        } finally {
          this.submitButton.removeAttribute(
            'aria-busy'
          );

          this.updateSubmitState();
        }
      }

      handleErrorMessage(message = '') {
        if (!this.errorMessage) {
          return;
        }

        this.errorMessage.toggleAttribute(
          'hidden',
          !message
        );

        this.errorMessage.textContent =
          message;
      }
    }
  );
}